import * as React from 'react';
import { IO } from 'fp-ts/lib/IO';

const isObject = (x: any): x is Object => 
    typeof(x) === 'object' && x !== null;

const isRef = <T extends HTMLElement>(x: React.Ref<T>): x is React.RefObject<T> => {
    return isObject(x) && 'current' in x;
}

export const useRefable = <T extends HTMLElement>(ref: React.Ref<T>) => {
    return isRef(ref) ? ref : React.useRef<T>(null);
}

export interface IORefHook<A> {
    read: () => A,
    write: (a: A) => IO<void>,
    modify: (f: (a: A) => A) => IO<void>,
}

export const useIORef: <A>(x: A) => IORefHook<A> = 
    (x) => {
        const currentValue = React.useRef(x);

        return {
            read: () => currentValue.current,
            write: (a) => () => currentValue.current = a,
            modify: (f) => () => currentValue.current = f(currentValue.current),
        };
    }

export const useRunOnlyFirst = (f: (...args: any[]) => any) => {
    const { read: isFirst, write } = useIORef(true);
    const render = write(false);

    return (...args: any[]) => {
        if(isFirst()){
            f(...args);
            render();
        }
    }
}

export const useIORefState: <T>(x: T) => [{ value: T, getValue: () => T }, (x: T) => void] = 
    (x) => {
        const { read: getValue, write } = useIORef(x);
        const [value, updateStateValue] = React.useState(x);

        return [
            { value, getValue },
            (x) => {
                write(x)();
                updateStateValue(x);
            }
        ];
    }


export const useUpdateEffect = (fn: () => void, deps: any[]) => {
    const { read: isInit, write } = useIORef(false);

    const initial = write(true);

    React.useEffect(() => {
        if(isInit()){
            fn();
        }
        else {
            initial();
        }
    }, [...deps]);
}

export const useErrorable: () => [boolean, () => void] = 
    () => {
        const [error, setError] = React.useState(false);

        const throwingError = () => setError(true);

        return [error, throwingError];
    }

export const useShowable = () => {
    const [isShowed, setShowed] = React.useState(false);
    
    const show = () => {
        setShowed(true);
    }

    const hide = () => {
        setShowed(false);
    }

    return {
        isShowed,
        show,
        hide,
    };
}


export const useLooper: (fn: Function) => [() => void, () => void] = 
    (fn) => {
        const { read: isStarted, write } = useIORef(false);
        const start = write(true);
        const stop = write(false);

        const core = () => {
            fn();

            if(isStarted()){
                window.requestAnimationFrame(() => core());
            }
        }

        const on = React.useCallback(() => {
            start();
            window.requestAnimationFrame(() => core());
        }, []);

        const off = React.useCallback(() => {
            stop();
        }, []);

        return [on, off];
    }

type S = number;

export type Signal = [S, (f: (...args: any[]) => void) => (...args: any[]) => void];

export const useSignal = (): Signal => {
    const [s, run] = React.useState(0);

    const runner = (f: (...args: any[]) => void) => {
        return (...args: any[]) => {
            f(...args);
            run(s => s + 1);
        }
    }

    return [s, runner];
}

export const useForceRender = () => {
    const [s, run] = React.useState(false);

    const runner = () => {
        run(!s);
    }

    return runner;
}

export interface SmartEvent<T> {
    handler: (e: T) => void,
    on: (handler: (e: T) => void) => void,
    off: (handler: (e: T) => void) => void,
    predicate: () => boolean,
    signal: any[],
}

export const useSmartEvent: <T>(p: SmartEvent<T>) => void = ({ handler, on, off, predicate, signal }) => {

    const { read: _on, write: updateOn } = useIORef(() => {});
    const { read: _off, write: updateOff } = useIORef(() => {});

    React.useEffect(() => {

        // вырубаем предыдущего слушателя
        _off()();

        // закидываем нового слушателя
        updateOn(() => {
            on(handler);
        })();
        
        // закидываем нового вырубателя слушателя
        updateOff(() => {
            off(handler);
        })();

        predicate() 
            ? _on()() 
            : _off()();


    }, [...signal]);
}
